import { api } from './api'
import { translate } from './i18n'

/**
 * Browser side of Web Push.
 *
 * The subtlety worth a comment: `applicationServerKey` wants raw bytes, and the
 * VAPID public key travels as base64url. Getting that conversion wrong produces a
 * subscription the push service silently refuses to deliver to, which is a
 * miserable thing to debug — hence one function, used once.
 */

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

function b64urlToBytes(value: string): Uint8Array {
  const padded =
    value.replace(/-/g, '+').replace(/_/g, '/') + '='.repeat((4 - (value.length % 4)) % 4)
  const binary = atob(padded)
  return Uint8Array.from(binary, (ch) => ch.charCodeAt(0))
}

function bytesToB64url(buffer: ArrayBuffer | null): string {
  if (!buffer) return ''
  let binary = ''
  for (const byte of new Uint8Array(buffer)) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export async function subscribeToPush(publicKey: string): Promise<void> {
  if (!pushSupported()) throw new Error(translate('notifications.browser_cannot_show_alerts'))

  const permission = await Notification.requestPermission()
  if (permission !== 'granted') {
    throw new Error(translate('notifications.allow_in_your_browser'))
  }

  const registration = await navigator.serviceWorker.ready
  const existing = await registration.pushManager.getSubscription()
  // A stale subscription from a previous VAPID key would be undeliverable, so it
  // is replaced rather than reused.
  if (existing) await existing.unsubscribe()

  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: b64urlToBytes(publicKey) as BufferSource,
  })

  await api.post('/api/notifications/push', {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: bytesToB64url(subscription.getKey('p256dh')),
      auth: bytesToB64url(subscription.getKey('auth')),
    },
  })
}

export async function unsubscribeFromPush(): Promise<void> {
  if (!pushSupported()) return
  const registration = await navigator.serviceWorker.ready
  const subscription = await registration.pushManager.getSubscription()
  if (!subscription) return
  await api.delete('/api/notifications/push', { endpoint: subscription.endpoint })
  await subscription.unsubscribe()
}

/**
 * Register the service worker. Failure is not worth surfacing — the app is fully
 * usable without it, and the only cost is a slower shell load and no push.
 */
export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator)) return
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined)
  })
}
